import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import studyService from '../../services/studyService';
import { Book, Hash, Globe, AlertCircle, ChevronLeft, Briefcase, Check } from 'lucide-react';

// Required field marker component for consistency
const RequiredFieldMarker = () => (
  <span className="text-red-500 ml-1" aria-label="required field">*</span>
);

// Character counter component
const CharacterCounter = ({ current, max }) => (
  <div className="text-xs text-gray-500 mt-1 text-right">
    {current.length} / {max} characters
  </div>
);

function CreateStudy() {
  const navigate = useNavigate();
  const [studyData, setStudyData] = useState({
    code: '',
    title: '',
    description: '',
    status: 'planned',
    is_byod_allowed: false,
    min_participant_id: '',
    max_participant_id: '',
  });

  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');

  // Constants for validation
  const LIMITS = {
    code: 20,
    title: 100,
    description: 500,
    participant_id: 10
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    
    if (type === 'checkbox') {
      setStudyData(prev => ({ ...prev, [name]: checked }));
      return;
    }

    // Validation for participant IDs (numbers only, max 10 chars)
    if (name === 'min_participant_id' || name === 'max_participant_id') {
      if (value === '' || (/^\d+$/.test(value) && value.length <= LIMITS.participant_id)) {
        setStudyData(prev => ({ ...prev, [name]: value }));
      }
      return;
    }

    // Character limits for other fields
    const limits = {
      code: LIMITS.code,
      title: LIMITS.title,
      description: LIMITS.description
    };

    if (limits[name] && value.length <= limits[name]) {
      setStudyData(prev => ({ ...prev, [name]: value }));
    } else if (!limits[name]) {
      setStudyData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!studyData.code.trim()) {
      newErrors.code = 'Study code is required';
    }

    if (!studyData.title.trim()) {
      newErrors.title = 'Study title is required';
    }

    if (studyData.min_participant_id && !studyData.max_participant_id) {
      newErrors.max_participant_id = 'Max participant ID is required when min ID is set';
    }

    if (!studyData.min_participant_id && studyData.max_participant_id) {
      newErrors.min_participant_id = 'Min participant ID is required when max ID is set';
    }

    if (studyData.min_participant_id && studyData.max_participant_id) {
      const min = parseInt(studyData.min_participant_id);
      const max = parseInt(studyData.max_participant_id);
      if (min >= max) {
        newErrors.min_participant_id = 'Min ID must be less than Max ID';
        newErrors.max_participant_id = 'Max ID must be greater than Min ID';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!validateForm()) {
      toast.error('Please correct the errors before submitting.');
      return;
    }

    try {
      const result = await studyService.createStudy(studyData);
      toast.success('Study created successfully!');
      setTimeout(() => navigate('/studies'), 500);
    } catch (err) {
      console.error('Error creating study:', err);
      const errorMessage = err.response?.data?.error || 'Failed to create study. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <Link
            to="/studies"
            className="mb-4 flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            <span>Back to Studies</span>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          <div className="px-6 py-4 bg-primary-600 dark:bg-primary-700">
            <h2 className="text-xl font-semibold text-white">Create New Study</h2>
          </div>
          <div className="px-6 py-8 sm:p-10">
            <div className="mb-6 text-sm text-gray-500">
              Fields marked with <RequiredFieldMarker /> are required
            </div>

            {error && (
              <div className="mb-4 p-4 bg-red-100 border-l-4 border-red-500 text-red-700">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  <p>{error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Study Title<RequiredFieldMarker />
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={studyData.title}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter the title of the study"
                  required
                />
                <CharacterCounter current={studyData.title} max={LIMITS.title} />
                {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={studyData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Provide a brief description of the study"
                />
                <CharacterCounter current={studyData.description} max={LIMITS.description} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                    Study Code<RequiredFieldMarker />
                  </label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      id="code"
                      name="code"
                      value={studyData.code}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.code ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="e.g., STU001"
                      required
                    />
                  </div>
                  <CharacterCounter current={studyData.code} max={LIMITS.code} />
                  {errors.code && <p className="mt-1 text-sm text-red-500">{errors.code}</p>}
                </div>

                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <select
                      id="status"
                      name="status"
                      value={studyData.status}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                    >
                      <option value="planned">Planned</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="min_participant_id" className="block text-sm font-medium text-gray-700 mb-1">
                    Min Participant ID
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      id="min_participant_id"
                      name="min_participant_id"
                      value={studyData.min_participant_id}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.min_participant_id ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="e.g., 1001"
                    />
                  </div>
                  {errors.min_participant_id && (
                    <p className="mt-1 text-sm text-red-500">{errors.min_participant_id}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="max_participant_id" className="block text-sm font-medium text-gray-700 mb-1">
                    Max Participant ID
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      id="max_participant_id"
                      name="max_participant_id"
                      value={studyData.max_participant_id}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.max_participant_id ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="e.g., 2000"
                    />
                  </div>
                  {errors.max_participant_id && (
                    <p className="mt-1 text-sm text-red-500">{errors.max_participant_id}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center">
                <label className="flex items-center cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      id="is_byod_allowed"
                      name="is_byod_allowed"
                      checked={studyData.is_byod_allowed}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 border-2 rounded-sm transition-colors duration-200 ease-in-out ${studyData.is_byod_allowed ? 'bg-primary-500 border-primary-500' : 'border-gray-300'}`}>
                      <Check className={`w-4 h-4 text-white transition-opacity duration-200 ease-in-out ${studyData.is_byod_allowed ? 'opacity-100' : 'opacity-0'}`} />
                    </div>
                  </div>
                  <span className="ml-2 text-sm text-gray-700">
                    Allow BYOD (Bring Your Own Device)
                  </span>
                </label>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => navigate('/studies')}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Create Study
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default CreateStudy;